











































import { getContentArea } from '@/contentful';
import {
  LoadingService,
  ExtendMixins,
} from '@/services';
import { EventBus } from '@/event-bus';
import { OpenAddDebitCardScreen } from '@/mixins/get-content-mixins';
import { PaymentMethodWrapper } from '@/components/account-settings-components/personal-settings-components';
import {
  DebitCardPaymentMethod,
  CrossoverPaymentMethod,
} from '@/components/account-settings-components/personal-settings-components/payment-method-components/payment-method-child-components';
import { PageIntro, PageSidebar } from '@/components/page-components';
import {
  MemberDependentResponseModel,
  MemberPaymentMethodsResponseModel,
  MemberDependentDebitCard,
  CTA, DebitCards,
} from '@/@types/member';
import moment from 'moment';

interface Data {
  sidebarHeading: string;
  sidebarContent: string;
}

export default ExtendMixins(
  OpenAddDebitCardScreen,
).extend({
  name: 'PaymentMethodPage',
  beforeRouteEnter(to, from, next) {
    LoadingService.watchPromises(
      getContentArea('payment-method-sidebar-content'),
    )
      .then((data) => {
        const sidebar = data[0];
        if (sidebar && sidebar.fields) {
          to.meta.sidebarHeading = sidebar.fields.headline;
          to.meta.sidebarContent = sidebar.fields.content;
        }
        next();
      })
      .catch(() => {
        EventBus.$emit('openModal', 'Error', {
          message: 'global_connection-error',
          action: {
            label: 'global_try-again',
            performAction() {
              location.reload();
            },
          },
          hideCancel: true,
        });
        EventBus.$emit('initialLoadDone');
      });
  },
  data(): Data {
    return {
      sidebarHeading: this.$route.meta.sidebarHeading,
      sidebarContent: this.$route.meta.sidebarContent,
    };
  },
  computed: {
    // Getters
    paymentMethods(): MemberPaymentMethodsResponseModel {
      return this.$store.getters.paymentMethods;
    },
    dependents(): MemberDependentResponseModel[] {
      return this.$store.getters.dependents;
    },
    groupTollFreePhone(): string {
      return this.$store.getters.groupTollFreePhone;
    },

    // Conditionals
    suppressDebitCardSection(): boolean {
      return !this.paymentMethods.canHaveCards;
    },
    suppressCrossoverSection(): boolean {
      return (
        !this.paymentMethods.canHaveMedicalCrossover &&
        !this.paymentMethods.canHaveDentalCrossover
      );
    },
    hasPrimaryCardholder(): boolean {
      return !!this.primaryCardholder;
    },
    displayCardName(): boolean {
      const selfDebitCards = this.debitCards.filter((card: MemberDependentDebitCard) => {
        return card.dependentType === 'Self';
      });
      return selfDebitCards.length > 1;
    },
    beforeStopCardDate(): boolean {
      const stopCardDate = this.paymentMethods ? moment(this.paymentMethods.stopCardDate) : '';
      const today = moment().startOf('day');
      return stopCardDate ? stopCardDate.isSameOrAfter(today) : false;
    },
    showCTAButton(): boolean {
      const showButton = !!(this.ctaButton.text && this.ctaButton.action);
      let suppressButton = false;
      const unavailableCards = this.debitCards.filter((card: MemberDependentDebitCard) => {
        return card.cardElection && card.suppressDependentCards;
      });
      if (unavailableCards.length === this.debitCards.length) {
        suppressButton = true;
      }
      return showButton && !suppressButton && this.beforeStopCardDate;
    },

    // Slots
    debitCardIsPrimarySection(): boolean {
      return !!(
        !this.suppressDebitCardSection &&
        !this.suppressCrossoverSection &&
        this.paymentMethods.cardElection
      );
    },
    crossoverIsPrimarySection(): boolean {
      return !!(
        !this.suppressDebitCardSection &&
        !this.suppressCrossoverSection &&
        !this.paymentMethods.cardElection &&
        (this.paymentMethods.medicalCrossoverElection ||
          this.paymentMethods.ptPCrossoverElection ||
          this.paymentMethods.dentalCrossoverElection)
      );
    },
    debitCardSlot(): string {
      return this.debitCardIsPrimarySection ||
      (!this.debitCardIsPrimarySection && !this.crossoverIsPrimarySection)
        ? 'primary'
        : 'secondary';
    },
    crossoverSlot(): string {
      return this.debitCardSlot === 'primary' ? 'secondary' : 'primary';
    },

    // Data
    debitCards(): DebitCards[] {
      const cardArray = [];
      if (this.paymentMethods.canHaveCards) {
        this.dependents.forEach((dep: MemberDependentResponseModel) => {
          dep.debitcards.forEach((card: MemberDependentDebitCard) => {
            cardArray.push({
              dependentId: dep.dependentId,
              dependentType: dep.dependentType,
              firstName: dep.firstName,
              middleName: dep.middleName,
              lastName: dep.lastName,
              dateOfBirth: dep.dateOfBirth,
              ...card,
            });
          });
        });
      }
      return cardArray;
    },
    activeDebitCards(): DebitCards[] {
      return this.debitCards.filter((card: MemberDependentDebitCard) => {
        return card.cardElection;
      });
    },
    activeCardholders(): DebitCards[] {
      const dependentTypeArray = ['Self', 'Spouse', 'Child'];
      return this.activeDebitCards
        .sort((a, b) => {
          return (
            dependentTypeArray.indexOf(a.dependentType) -
            dependentTypeArray.indexOf(b.dependentType)
          );
        })
        .sort((a, b) => {
          if (
            dependentTypeArray.indexOf(a.dependentType) ===
            dependentTypeArray.indexOf(b.dependentType)
          ) {
            if (a.firstName.toLowerCase() < b.firstName.toLowerCase()) {
              return -1;
            }
            if (a.firstName.toLowerCase() > b.firstName.toLowerCase()) {
              return 1;
            }
          }
        });
    },
    primaryCardholder(): DebitCards {
      return this.activeCardholders.find((dependent) => {
        return dependent.dependentType === 'Self';
      });
    },
    primaryDebitCards(): DebitCards[] {
      return this.activeDebitCards.filter((card: MemberDependentDebitCard) => {
        return card.dependentType === 'Self';
      });
    },
    ctaButton(): CTA {
      if (!this.suppressDebitCardSection) {
        return !this.hasPrimaryCardholder
          ? {
            text: this.$options.filters.t(['debit-card_action_request-card']),
            action: this.openAddDebitCardScreen,
          }
          : {
            text: this.$options.filters.t(['debit-card_action_add-card']),
            action: this.openAddDebitCardScreen,
          };
      }
      return {} as CTA;
    },
  },
  components: {
    PaymentMethodWrapper,
    DebitCardPaymentMethod,
    CrossoverPaymentMethod,
    PageIntro,
    PageSidebar,
  },
});
